import { useState, useEffect } from "react";
import API_BASE_URL from "../../config";

const Profile = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    preferredName: "",
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [isEditing, setIsEditing] = useState({
    firstName: false,
    lastName: false,
    email: false,
    preferredName: false,
  });
  const [fieldErrors, setFieldErrors] = useState({
    firstName: "",
    lastName: "",
    email: "",
    preferredName: "",
  });

  const fetchAccountInfo = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/account-info/`, {
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error('Failed to fetch account information');
      }
      const data = await response.json();
      setFormData((prev) => ({ ...prev, ...data }));
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchAccountInfo();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditClick = (field) => {
    setIsEditing({
      firstName: false,
      lastName: false,
      email: false,
      preferredName: false,
      [field]: true
    });
  };

  const handleCancelClick = (field) => {
    setIsEditing({
      firstName: false,
      lastName: false,
      email: false,
      preferredName: false
    });
    // Clear any errors for this field
    setFieldErrors(prev => ({ ...prev, [field]: "" }));
    fetchAccountInfo(); // Re-fetch data to reset changes
  };

  const handleSaveClick = async (field) => {
    try {
      // Clear any previous errors for this field
      setFieldErrors(prev => ({ ...prev, [field]: "" }));
      
      // Implement the API call to update the field in the backend
      const response = await fetch(`${API_BASE_URL}/api/update-field/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ field, value: formData[field] }),
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json();
        
        // Handle specific error messages
        if (field === 'email' && errorData.error === 'Email already in use by another account') {
          setFieldErrors(prev => ({ ...prev, email: "This email is already in use by another account." }));
          return;
        }
        
        throw new Error(errorData.error || 'Failed to update field');
      }

      setIsEditing((prev) => ({ ...prev, [field]: false }));
    } catch (error) {
      console.error(error);
      setFieldErrors(prev => ({ ...prev, [field]: error.message }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Implement update logic here
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      alert("New passwords do not match.");
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/update-password/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(passwordData),
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json();
        alert(errorData.error || "Failed to update password.");
        return;
      }

      alert("Password updated successfully.");
      setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (error) {
      console.error(error);
      alert("An error occurred while updating the password.");
    }
  };

  return (
    <main className="min-h-[calc(100vh-56px)] bg-gray-100 p-6 text-gray-600">
      <div className="bg-white border border-gray-300 rounded-lg p-6 mb-8">
        <h2 className="text-xl font-bold mb-4">Account Management</h2>
        <div className="space-y-6">
          {/* User Information */}
          {['firstName', 'lastName', 'email', 'preferredName'].map((key) => (
            <div key={key} className="space-y-2">
              <label className="text-sm font-medium capitalize">{key}</label>
              <div className="flex flex-wrap gap-3">
                <div className="flex-grow">
                  <input
                    type="text"
                    name={key}
                    value={formData[key]}
                    onChange={handleInputChange}
                    className={`w-full border ${fieldErrors[key] ? 'border-red-500' : 'border-gray-300'} rounded p-1 bg-white`}
                    disabled={!isEditing[key]}
                  />
                  {fieldErrors[key] && (
                    <p className="text-red-500 text-sm mt-1">{fieldErrors[key]}</p>
                  )}
                </div>
                {isEditing[key] ? (
                  <>
                    <button
                      type="button"
                      onClick={() => handleSaveClick(key)}
                      className="px-6 py-2 bg-primary hover:bg-primary-light text-white rounded-md">
                      Save
                    </button>
                    <button
                      type="button"
                      onClick={() => handleCancelClick(key)}
                      className="px-6 py-2 bg-gray-500 hover:bg-gray-400 text-white rounded-md">
                      Cancel
                    </button>
                  </>
                ) : (
                  !Object.values(isEditing).includes(true) && (
                    <button
                      type="button"
                      onClick={() => handleEditClick(key)}
                      className="px-6 py-2 bg-primary hover:bg-primary-light text-white rounded-md">
                      Edit
                    </button>
                  )
                )}
              </div>
            </div>
          ))}

          {/* Password Update */}
          {!Object.values(isEditing).includes(true) && (
            <>
              <h3 className="text-lg font-bold mt-6">Change Password</h3>
              <form onSubmit={handlePasswordSubmit} className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Current Password</label>
                  <input
                    type="password"
                    name="currentPassword"
                    value={passwordData.currentPassword}
                    onChange={handlePasswordChange}
                    className="w-full border border-gray-300 rounded p-1 bg-white"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">New Password</label>
                  <input
                    type="password"
                    name="newPassword"
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    className="w-full border border-gray-300 rounded p-1 bg-white"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Confirm New Password</label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    className="w-full border border-gray-300 rounded p-1 bg-white"
                    required
                  />
                </div>
                <button
                  type="submit"
                  className="px-6 py-2 bg-primary hover:bg-primary-light text-white rounded-md">
                  Update Password
                </button>
              </form>
            </>
          )}

          {/* Plan Usage */}
          <h3 className="text-lg font-bold mt-6">Plan Usage</h3>
          <p>Upload Limit: {formData.usageInfo?.upload_limit} hours</p>
          <p>Used: {formData.usageInfo?.used_time} hours</p>
          <p>Remaining: {formData.usageInfo?.remaining_time} hours</p>
          {!formData.usageInfo?.can_upload && (
            <p className="text-red-600">
              You have exceeded your monthly upload limit. Upgrade your plan to continue uploading.
            </p>
          )}
        </div>
      </div>
    </main>
  );
};

export default Profile;
